import { captureException } from '@sentry/react'
import { Button } from '@themenu/design-system'
import { type FC, useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { environmentVariables } from '../../../utils/environmentVariables'
import { useLocation, useNavigate } from 'react-router-dom'

import { Layout } from '../../../components/Shared/Layout'
import * as brActivation from '../../../services/api/brActivation'
import { saveTokens } from '../../../services/authentication'
import { logEvent } from '../../../services/datadog'
import { SecondaryFactor } from '../../../types'
import type {
  BrActivationChannel,
  BrActivationData,
  BrActivationStep,
} from '../../../types/brActivation'
import {
  BrActivationSessionExpiredError,
  InvalidCodeError,
  InvalidPhoneError,
  InvalidPinError,
  PasswordPolicyError,
  PinUnavailableError,
} from '../../../utils/errors'
import { ActivationOtpForm } from './ActivationOtpForm'
import { ActivationStepLayout } from './ActivationStepLayout'
import { ContactEntryStep } from './ContactEntryStep'
import { BrCredentialsForm } from './CredentialsForm'
import { PasswordStep } from './PasswordStep'
import { NamesStep } from './NamesStep'
import { SuccessStep } from './SuccessStep'

const BR_SUPPORT_REQUEST_URL = `${environmentVariables('BR_HELP_URL')}/requests/new`

/**
 * Server-driven orchestrator for the BR **activation** funnel (contract §6/§7).
 *
 * Mirrors how `SignInPage` conditionally renders its sub-forms, but advances
 * PURELY on the `step` token returned by the back-end — step order is never
 * hardcoded (phone/email are conditional, the server decides). On mount it calls
 * `GET /state` to resume an in-progress funnel; the funnel entry point is now the
 * standard `/signin` on-ramp, so a 401 (no/expired BR cookie) or any resume
 * failure redirects to `/signin` rather than rendering an in-app entry step.
 */

type FlowState = {
  step: BrActivationStep
  data: BrActivationData
}

type ActivationFlowProps = {
  // Post-finalize "Continue" strategy, injected by `BrActivationRoute` and branched
  // on the originating platform there (web → employee app, native → OAuth callback
  // that closes the iOS/Android auth webview). Called with the finalize access token
  // (or `null` when the BE token shipped in parallel) — the funnel itself stays
  // platform-agnostic and never touches the redirect logic.
  onComplete: (accessToken: string | null) => void | Promise<void>
}

export const ActivationFlow: FC<ActivationFlowProps> = ({ onComplete }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { formatMessage } = useIntl()

  // `null` until `GET /state` resolves — no step renders before resume completes
  // (the `resuming` flag gates the render to a bare `<Layout hideFooter />`).
  const [state, setState] = useState<FlowState | null>(null)
  const [resuming, setResuming] = useState(true)
  const [errorMessageId, setErrorMessageId] = useState<string | null>(null)
  // Bumped by the finalize-error retry button to re-run the finalize effect
  // (its guard keys on `state.step === 'finalize'`, which doesn't change on a
  // failed attempt, so we need an explicit re-trigger). Capped at
  // MAX_FINALIZE_ATTEMPTS to avoid infinite retries on permanent errors (e.g.
  // 409 already-activated): after exhaustion the UI routes to care.
  const MAX_FINALIZE_ATTEMPTS = 3
  const [finalizeAttempt, setFinalizeAttempt] = useState(0)

  // Access token minted at finalize (contract §13). It is saved to cookies in the
  // finalize effect (like login/WebAuthn), but the native success branch also needs
  // it in-memory to replay the OAuth authorization-code exchange (`/session`) — the
  // finalize envelope is gone by the time the user clicks "Continue". May stay null
  // when the BE token ships in parallel (the finalize guard tolerates a missing token).
  const [finalizeAccessToken, setFinalizeAccessToken] = useState<string | null>(null)

  // Identifier typed at `/signin`, carried into the funnel via React Router /
  // history.state (FE-only, no contract change). Captured once at mount from
  // `location.state`: it pre-fills the disabled PIN-step field and gates the PIN
  // page (req-3 — never show PIN without an identifier). history.state is
  // in-memory (not web-storage) so it works on storage-blocked devices and RR v6
  // rehydrates `location.state` on refresh, so the identifier survives a reload.
  // FR/SSO users never carry this state.
  const [identifier] = useState<string>(() => (location.state as { identifier?: string } | null)?.identifier ?? '')

  // Last contact value entered, so resend on the phone/email OTP step can
  // re-trigger the same endpoint with the same value.
  const lastPhone = useRef<string>('')
  const lastEmail = useRef<string>('')

  // Client-side history stack of visited screens (FE-only, no contract change).
  // The funnel is server-driven (the next step comes from the server `next`) and
  // phone/email are conditional, so a single back-pointer is not enough: we keep a
  // stack of full `FlowState`s (step + retained data) to restore the right previous
  // screen WITH its data on back. Stack lives in a ref — nothing renders off it, so
  // it must not trigger re-renders (and a ref dodges the StrictMode double-invoke of
  // a setState updater). `stateRef` mirrors `state` so the forward push can read the
  // current screen without making `applyState` depend on `state` (which would churn
  // its identity and re-fire the mount/finalize effects that list it in their deps).
  // IMPORTANT: `stateRef.current` is updated synchronously (not via useEffect) in
  // both `applyState` and `onBack` so it is never stale when the next call reads it.
  const stateRef = useRef<FlowState | null>(null)
  const historyRef = useRef<FlowState[]>([])

  const goToSignin = useCallback(() => {
    // Re-prefill the `/signin` username field from the carried identifier via
    // router state (CredentialsForm reads `location.state.username`). Covers the
    // PIN back button, session-expired, and resume-failure paths. SPA navigation
    // keeps the `lookup` cookie — no web-storage involved. `replace: true` mirrors
    // the on-ramp (and the former `window.location.replace`): the funnel and
    // `/signin` swap in a single history slot, so browser-back doesn't re-mount the
    // funnel and re-trigger `GET /state`.
    navigate('/signin', { replace: true, state: { username: identifier } })
  }, [navigate, identifier])

  // Forward transition. Pushes the CURRENT screen onto the history stack only when
  // the step actually changes (never on error re-renders — those only set
  // `errorMessageId` and never call this). Kept referentially stable (empty deps).
  const applyState = useCallback((step: BrActivationStep, data: BrActivationData = {}) => {
    setErrorMessageId(null)
    const previous = stateRef.current
    if (previous && previous.step !== step) {
      historyRef.current = [...historyRef.current, previous]
    }
    const next = { step, data }
    stateRef.current = next
    setState(next)
  }, [])

  // Back = pop the previous screen and re-render it WITH its retained data, WITHOUT
  // re-pushing (separate path from `applyState`, so no loop and no forward side
  // effects). Empty stack → `/signin` (e.g. on resume the stack is empty by design).
  const onBack = useCallback(() => {
    const stack = historyRef.current
    if (stack.length === 0) {
      goToSignin()
      return
    }
    const previous = stack[stack.length - 1]
    historyRef.current = stack.slice(0, -1)
    stateRef.current = previous
    setErrorMessageId(null)
    setState(previous)
  }, [goToSignin])

  // Resume an in-progress funnel. 401 → not in a funnel → start at step 1.
  useEffect(() => {
    let cancelled = false
    brActivation
      .fetchState()
      .then((resumed) => {
        if (cancelled) {
          return
        }
        // req-3: never display the PIN page without an identifier — bounce to
        // `/signin` before the empty PIN form can flash. Guard is scoped to the
        // PIN step only; later steps don't surface the identifier.
        if (resumed.step === 'pin' && !identifier) {
          goToSignin()
          return
        }
        applyState(resumed.step, resumed.data)
      })
      .catch((error) => {
        if (cancelled) {
          return
        }
        if (error instanceof BrActivationSessionExpiredError) {
          // Token expired (401): clear it so the stale cookie doesn't block the
          // next attempt.
          brActivation.clearFunnelToken()
        } else {
          // Transient/5xx error: keep the token — the user may still resume if
          // they re-enter the funnel while the stage JWT is still valid.
          captureException(error)
          logEvent('br_activation_resume_error', undefined, {}, error as Error, 'error')
        }
        goToSignin()
      })
      .finally(() => {
        if (!cancelled) {
          setResuming(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [applyState, identifier, goToSignin])

  // Centralised error handling: typed errors → generic ids; expired → /signin.
  const handleError = useCallback(
    (error: unknown, genericId: string): void => {
      if (error instanceof BrActivationSessionExpiredError) {
        brActivation.clearFunnelToken()
        logEvent('br_activation_session_expired', undefined, {}, undefined, 'warn')
        goToSignin()
        return
      }
      if (error instanceof InvalidPinError) {
        setErrorMessageId('br_activation.pin.error')
        return
      }
      if (error instanceof PinUnavailableError) {
        setErrorMessageId('br_activation.pin.unavailable_error')
        return
      }
      if (error instanceof InvalidCodeError) {
        setErrorMessageId('br_activation.otp.error')
        return
      }
      if (error instanceof InvalidPhoneError) {
        setErrorMessageId('br_activation.phone.care_error')
        return
      }
      if (error instanceof PasswordPolicyError) {
        setErrorMessageId('br_activation.password.error')
        return
      }
      captureException(error)
      logEvent('br_activation_unknown_error', undefined, {}, error as Error, 'error')
      setErrorMessageId(genericId)
    },
    [goToSignin],
  )

  // ---- PIN ----------------------------------------------------------------
  const onPinSubmit = useCallback(
    async (pin: string): Promise<void> => {
      try {
        const next = await brActivation.submitPin(pin)
        if (next.step === 'contact_care') {
          setErrorMessageId('br_activation.pin.contact_care_error')
          return
        }
        applyState(next.step, next.data)
        // The first MFA code is sent here, on the genuine pin → otp transition
        // (contract §6 — `/pin` returns `next:"otp"` + channels but does NOT send).
        // Doing it here rather than on the otp mount means a `GET /state` resume or
        // a browser refresh that lands on `otp` does NOT trigger another send (the
        // code was already sent before the reload), avoiding duplicate SMS/email and
        // server-side rate-limit lockouts.
        if (next.step === 'otp') {
          const otpChannels = next.data.channels ?? []
          const initialChannel: BrActivationChannel = otpChannels.includes(SecondaryFactor.EMAIL)
            ? SecondaryFactor.EMAIL
            : (otpChannels[0] ?? SecondaryFactor.EMAIL)
          brActivation.sendSecurityCode(initialChannel).catch((error) => {
            logEvent('br_activation_mfa_initial_send_error', undefined, {}, error as Error, 'warn')
          })
        }
      } catch (error) {
        handleError(error, 'br_activation.pin.error')
      }
    },
    [applyState, handleError],
  )

  // ---- Step 3 (MFA) -------------------------------------------------------
  const onMfaResend = useCallback(async (channel: BrActivationChannel): Promise<void> => {
    await brActivation.sendSecurityCode(channel)
  }, [])

  // The OTP form passes the ACTIVE channel (post-switch), so we verify against
  // the channel the code was actually sent on (fixes the POC switch pitfall).
  const onMfaSubmit = useCallback(
    async (code: string, channel: BrActivationChannel): Promise<void> => {
      try {
        const next = await brActivation.verifySecurityCode(channel, code)
        applyState(next.step, next.data)
      } catch (error) {
        handleError(error, 'br_activation.otp.error')
      }
    },
    [applyState, handleError],
  )

  // ---- Step 7 (password) --------------------------------------------------
  const onPasswordSubmit = useCallback(
    async (password: string, confirmation: string): Promise<void> => {
      try {
        const next = await brActivation.submitPassword(password, confirmation)
        applyState(next.step, next.data)
      } catch (error) {
        handleError(error, 'br_activation.password.error')
      }
    },
    [applyState, handleError],
  )

  // ---- Step 6 (names) --------------------------------------------------
  const onNamesSubmit = useCallback(
    async (personal: { given_name: string; family_name: string; title?: string }): Promise<void> => {
      try {
        const next = await brActivation.submitNames(personal)
        applyState(next.step, next.data)
      } catch (error) {
        handleError(error, 'br_activation.personal.error')
      }
    },
    [applyState, handleError],
  )

  // ---- Step 5 (phone) -----------------------------------------------------
  const onPhoneSubmit = useCallback(
    async (phone: string): Promise<void> => {
      try {
        lastPhone.current = phone
        const next = await brActivation.submitPhone(phone)
        applyState(next.step, next.data)
      } catch (error) {
        handleError(error, 'br_activation.phone.error')
      }
    },
    [applyState, handleError],
  )

  const onPhoneVerify = useCallback(
    async (code: string): Promise<void> => {
      try {
        const next = await brActivation.verifyPhone(code, lastPhone.current)
        applyState(next.step, next.data)
      } catch (error) {
        handleError(error, 'br_activation.otp.error')
      }
    },
    [applyState, handleError],
  )

  // Single-channel SMS resend: re-post the same phone (no channel switch).
  const onPhoneResend = useCallback(async (): Promise<void> => {
    await brActivation.submitPhone(lastPhone.current)
  }, [])

  // ---- Step 4 (email) -----------------------------------------------------
  const onEmailSubmit = useCallback(
    async (email: string): Promise<void> => {
      try {
        lastEmail.current = email
        const next = await brActivation.initEmail(email)
        applyState(next.step, next.data)
      } catch (error) {
        handleError(error, 'br_activation.email.error')
      }
    },
    [applyState, handleError],
  )

  const onEmailVerify = useCallback(
    async (code: string): Promise<void> => {
      try {
        const next = await brActivation.verifyEmail(code, lastEmail.current)
        applyState(next.step, next.data)
      } catch (error) {
        handleError(error, 'br_activation.otp.error')
      }
    },
    [applyState, handleError],
  )

  // Single-channel email resend: re-post the same email (no channel switch).
  const onEmailResend = useCallback(async (): Promise<void> => {
    await brActivation.initEmail(lastEmail.current)
  }, [])

  // ---- Finalize -----------------------------------------------------------
  useEffect(() => {
    if (state?.step !== 'finalize') {
      return
    }
    let cancelled = false
    brActivation
      .finalize()
      .then((next) => {
        if (!cancelled) {
          logEvent('br_activation_finalized')
          // Auth tokens minted at finalize (contract §13): save them synchronously
          // (writes `lunchr:jwt` + `lunchr:rt` on JWT_COOKIES_DOMAIN, exactly like
          // normal/WebAuthn login) BEFORE transitioning to `success`, so the cookie
          // is set on the directory origin before `SuccessStep` renders and the
          // user continues to EMPLOYEE_APP_URL — otherwise employee-bff returns 401.
          // Guarded because
          // the BE token is deployed in parallel: a missing token still redirects
          // (unauthenticated) rather than throwing into the finalize error path.
          if (next.data.token) {
            saveTokens(next.data.token.access_token, next.data.token.refresh_token)
            // Retain the access token in-memory for the native success branch, which
            // replays the OAuth authorization-code exchange on "Continue".
            setFinalizeAccessToken(next.data.token.access_token)
          }
          // Funnel is complete — clear the stage JWT cookie before transitioning.
          brActivation.clearFunnelToken()
          applyState(next.step, next.data)
        }
      })
      .catch((error) => {
        if (!cancelled) {
          handleError(error, 'br_activation.finalize.error')
        }
      })
    return () => {
      cancelled = true
    }
  }, [state?.step, finalizeAttempt, applyState, handleError])

  // Retry a failed finalize: clear the error and bump the attempt counter so the
  // finalize effect re-runs (the user is still on the `finalize` step). Without
  // this a transient finalize failure would dead-end on a blank screen.
  const onFinalizeRetry = useCallback(() => {
    if (finalizeAttempt >= MAX_FINALIZE_ATTEMPTS) return
    setErrorMessageId(null)
    setFinalizeAttempt((attempt) => attempt + 1)
  }, [finalizeAttempt, MAX_FINALIZE_ATTEMPTS])

  // Post-finalize "Continue" handler: forward the captured finalize access token to
  // the injected, platform-aware `onComplete` (resolved by `BrActivationRoute`). The
  // funnel stays agnostic — it does not know web from native. Returning the promise
  // keeps `SuccessStep`'s in-flight disable meaningful across the native OAuth replay.
  const onSuccessContinue = useCallback(
    (): void | Promise<void> => onComplete(finalizeAccessToken),
    [onComplete, finalizeAccessToken],
  )

  const channels = state?.data.channels ?? []
  const mfaChannel: BrActivationChannel = channels.includes(SecondaryFactor.EMAIL)
    ? SecondaryFactor.EMAIL
    : (channels[0] ?? SecondaryFactor.EMAIL)

  if (resuming || !state) {
    return <Layout hideFooter />
  }

  // Back is suppressed on any step whose immediate predecessor in the history
  // stack is `otp`: once MFA is passed the session advances to `authenticated`
  // and the `credentialed` cookie is gone — re-doing OTP is not possible.
  // Deriving this at render time (from the ref, already updated synchronously by
  // `applyState`) means the rule is funnel-order-agnostic: if steps are ever
  // reordered, any step landing right after `otp` is automatically protected.
  const showBackButton = historyRef.current.at(-1)?.step !== 'otp'

  return (
    <Layout>
      {state.step === 'pin' && (
        <BrCredentialsForm
          onPinSubmit={onPinSubmit}
          errorMessageId={errorMessageId}
          errorFormatValues={
            errorMessageId === 'br_activation.pin.contact_care_error'
              ? {
                  link: (chunks) => (
                    <a
                      href={BR_SUPPORT_REQUEST_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'inherit' }}
                      className="font-medium underline visited:text-inherit"
                    >
                      {chunks}
                    </a>
                  ),
                }
              : undefined
          }
          onBack={goToSignin}
        />
      )}

      {state.step === 'otp' && (
        <ActivationOtpForm
          length={6}
          variant="text"
          channel={mfaChannel}
          channels={channels}
          obfuscatedRecipients={state.data.obfuscated_recipients}
          titleId={`br_activation.mfa.title.${mfaChannel}`}
          messageId="br_activation.mfa.message"
          onSubmit={onMfaSubmit}
          onResend={onMfaResend}
          errorMessageId={errorMessageId}
        />
      )}

      {state.step === 'password' && (
        <PasswordStep onSubmit={onPasswordSubmit} errorMessageId={errorMessageId} showBackButton={showBackButton} onBack={onBack} />
      )}

      {state.step === 'names' && (
        <NamesStep
          imported={state.data.imported}
          onSubmit={onNamesSubmit}
          errorMessageId={errorMessageId}
          showBackButton={showBackButton}
          onBack={onBack}
        />
      )}

      {state.step === 'phone' && (
        <ContactEntryStep
          kind="phone"
          initialValue={lastPhone.current}
          onSubmit={onPhoneSubmit}
          errorMessageId={errorMessageId}
          showBackButton={showBackButton}
          onBack={onBack}
        />
      )}

      {state.step === 'phone_otp' && (
        <ActivationOtpForm
          length={6}
          variant="text"
          channel={SecondaryFactor.SMS}
          maskedRecipient={lastPhone.current || state.data.obfuscated_recipients?.phone}
          titleId="br_activation.phone.otp.title"
          messageId="br_activation.phone.otp.message"
          onSubmit={onPhoneVerify}
          onResend={onPhoneResend}
          errorMessageId={errorMessageId}
          errorActions={
            errorMessageId === 'br_activation.phone.care_error' ? (
              <Button
                variant="alternativeText"
                component="a"
                href={environmentVariables('BR_HELP_URL')}
                target="_blank"
                rel="noopener noreferrer"
              >
                {formatMessage({ id: 'br_activation.phone.care_error.link' })}
              </Button>
            ) : undefined
          }
          showBackButton={showBackButton}
          onBack={onBack}
        />
      )}

      {state.step === 'email' && (
        <ContactEntryStep
          kind="email"
          initialValue={lastEmail.current}
          onSubmit={onEmailSubmit}
          errorMessageId={errorMessageId}
          showBackButton={showBackButton}
          onBack={onBack}
        />
      )}

      {state.step === 'email_otp' && (
        <ActivationOtpForm
          length={6}
          variant="text"
          channel={SecondaryFactor.EMAIL}
          maskedRecipient={lastEmail.current || state.data.obfuscated_recipients?.email}
          titleId="br_activation.email.otp.title"
          messageId="br_activation.email.otp.message"
          onSubmit={onEmailVerify}
          onResend={onEmailResend}
          errorMessageId={errorMessageId}
          showBackButton={showBackButton}
          onBack={onBack}
        />
      )}

      {/* `finalize` has no dedicated form: the effect above runs `finalize()` and
          advances to `success`. While in flight the step renders nothing (a brief
          transient). On failure we surface an error screen with a retry button so
          the user is never dead-ended on a blank screen. */}
      {state.step === 'finalize' && errorMessageId && (
        <ActivationStepLayout
          titleId="br_activation.finalize.error_title"
          errorMessageId={errorMessageId}
          testId="br-activation-finalize-error"
          centered
          footer={
            finalizeAttempt >= MAX_FINALIZE_ATTEMPTS ? (
              <Button
                data-testid="br-activation-finalize-care"
                variant="primary"
                component="a"
                href={environmentVariables('BR_HELP_URL')}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full"
              >
                {formatMessage({ id: 'br_activation.phone.care_error.link' })}
              </Button>
            ) : (
              <Button
                data-testid="br-activation-finalize-retry"
                variant="primary"
                className="w-full"
                onClick={onFinalizeRetry}
              >
                {formatMessage({ id: 'br_activation.finalize.retry' })}
              </Button>
            )
          }
        />
      )}

      {state.step === 'success' && <SuccessStep onContinue={onSuccessContinue} />}
    </Layout>
  )
}
